import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import './Profiles.css';

const Concesionarios = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [concesionarios, setConcesionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [showModal, setShowModal] = useState(false);
  const [editingConcesionario, setEditingConcesionario] = useState(null);
  const [transitos, setTransitos] = useState([]);
  const [associatedTransits, setAssociatedTransits] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    nit: '',
    address: '',
    phone: '',
    status: 'activo'
  });

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/dashboard');
    }
    fetchConcesionarios();
    fetchTransitos();
  }, [user, navigate]);

  const fetchTransitos = async () => {
    try {
      const res = await axios.get('/api/transitos');
      setTransitos(res.data);
    } catch (err) {
      console.error('Error al cargar tránsitos:', err);
    }
  };

  const fetchConcesionarios = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/concesionarios');
      setConcesionarios(res.data);
      setError('');
    } catch (err) {
      setError('Error al cargar concesionarios');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (concesionario = null) => {
    if (concesionario) {
      setEditingConcesionario(concesionario);
      setFormData({
        name: concesionario.name,
        nit: concesionario.nit || '',
        address: concesionario.address || '',
        phone: concesionario.phone || '',
        status: concesionario.status
      });
      try {
        const res = await axios.get(`/api/concesionarios/${concesionario.id}/transitos`);
        setAssociatedTransits(res.data);
      } catch (err) {
        console.error('Error al cargar tránsitos asociados:', err);
        setAssociatedTransits([]);
      }
    } else {
      setEditingConcesionario(null);
      setFormData({
        name: '',
        nit: '',
        address: '',
        phone: '',
        status: 'activo'
      });
      setAssociatedTransits([]);
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      let concessionaireId = editingConcesionario?.id;
      if (editingConcesionario) {
        await axios.put(`/api/concesionarios/${editingConcesionario.id}`, formData);
      } else {
        const res = await axios.post('/api/concesionarios', formData);
        concessionaireId = res.data.id;
      }
      
      // Guardar asociaciones
      await axios.post(`/api/concesionarios/${concessionaireId}/transitos`, {
        transito_ids: associatedTransits
      });
      
      fetchConcesionarios();
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar concesionario');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Lógica de paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = concesionarios.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Navbar title="🏪 Gestión de Concesionarios (Tramitadores)" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}

          <div className="page-header">
            <div className="header-title">
              <h2>Concesionarios Registrados</h2>
              <p className="subtitle">Empresas encargadas de iniciar trámites</p>
            </div>
            <button onClick={() => handleOpenModal()} className="btn-primary">
              ➕ Nuevo Concesionario
            </button>
          </div>

          {loading ? (
            <p className="loading">Cargando concesionarios...</p>
          ) : (
            <div className="table-wrapper">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>NIT</th>
                    <th>Dirección</th>
                    <th>Teléfono</th>
                    <th>Tránsitos Autorizados</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(c => (
                    <tr key={c.id}>
                      <td className="user-name">{c.name}</td>
                      <td>{c.nit}</td>
                      <td>{c.address}</td>
                      <td>{c.phone}</td>
                      <td>
                        <span className="profile-badge" style={{ display: 'inline-block', minWidth: '24px', textAlign: 'center' }}>
                          🏢 {c.transitos_count || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`status ${c.status}`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="actions">
                        <button 
                          onClick={() => handleOpenModal(c)} 
                          className="btn-action btn-edit"
                          title="Editar Concesionario"
                        >
                          ✏️ Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <Pagination 
                currentPage={currentPage}
                totalItems={concesionarios.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{editingConcesionario ? 'Editar Concesionario' : 'Nuevo Concesionario'}</h3>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label>Nombre del Concesionario</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>NIT</label>
                <input
                  type="text"
                  name="nit"
                  value={formData.nit}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Dirección</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Teléfono</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              {editingConcesionario && (
                <div className="form-group">
                  <label>Estado</label>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
              )}

              <div className="form-group" style={{ marginTop: '20px' }}>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                  🏢 Tránsitos Autorizados para Envío de Trámites
                </label>
                <div style={{ 
                  maxHeight: '150px', 
                  overflowY: 'auto', 
                  border: '1px solid var(--card-border)', 
                  borderRadius: '6px', 
                  padding: '10px',
                  background: 'var(--card-bg)'
                }}>
                  {transitos.map(t => (
                    <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', fontSize: '13px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={associatedTransits.includes(t.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssociatedTransits(prev => [...prev, t.id]);
                          } else {
                            setAssociatedTransits(prev => prev.filter(id => id !== t.id));
                          }
                        }}
                      />
                      🏢 {t.name}
                    </label>
                  ))}
                  {transitos.length === 0 && (
                    <span style={{ fontSize: '12px', color: '#64748b' }}>No hay tránsitos registrados.</span>
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingConcesionario ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Concesionarios;
